import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { postApi } from "../../services/apiServices";
import { toast } from "react-toastify";
import { isAuthenticated } from "../../Utils/authUtils";

function Login() {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [emailActive, setEmailActive] = useState(false);
  const [passwordActive, setPasswordActive] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });
  const [touched, setTouched] = useState({ email: false, password: false });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { 
    const checkExistingAuth = () => {
      try {
        if (isAuthenticated()) {
          navigate("/admin/dashboard");
        }
      } catch (error) {
        console.error('Error checking existing auth:', error);
       
        localStorage.removeItem("userDetail");
        localStorage.removeItem("token");
      }
    };
    
    checkExistingAuth();
  }, [navigate]); 

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email) return "Email is required.";
    if (!emailRegex.test(email)) return "Please enter a valid email address.";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required.";
    if (password.length < 6) return "Password must be at least 6 characters.";
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, email: value }));
    if (touched.email) {
      setErrors(prev => ({ ...prev, email: validateEmail(value) }));
    }
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, password: value }));
    if (touched.password) {
      setErrors(prev => ({ ...prev, password: validatePassword(value) }));
    }
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    if (field === 'email') {
      setErrors(prev => ({ ...prev, email: validateEmail(formData.email) }));
    } else if (field === 'password') {
      setErrors(prev => ({ ...prev, password: validatePassword(formData.password) }));
    }
  };

  const validateForm = () => {
    const emailError = validateEmail(formData.email);
    const passwordError = validatePassword(formData.password);
    
    setErrors({
      email: emailError,
      password: passwordError
    });
    
    setTouched({
      email: true,
      password: true
    });

    return !emailError && !passwordError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await postApi("login", { 
        email: formData.email, 
        password: formData.password 
      });
        console.log(response, "login response ")
      if (response?.statusCode === 200) {
        toast.success(response.message || "Login successful!");
        localStorage.setItem("userDetail", JSON.stringify(response?.user_details));
        localStorage.setItem("token", JSON.stringify(response?.user_details?.token));
        navigate("/dashboard");
      } else {
        toast.error(response.message || "Login failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <>
    <main className="login">
      <div className="login-left">
        <div className="login-left-inner">
          <div className="logi-logo">
            <img src="/images/logo.svg" alt="logo" />
          </div>
          <div className="login-mid">
            <h3>Login</h3>
            <p>Please enter your admin credentials below.</p>

            <form onSubmit={handleSubmit}>
              <div className={`form-group ${emailActive ? "active" : ""}`}>
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  className={`form-control ${errors.email && touched.email ? "is-invalid" : ""}`}
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleEmailChange}
                  onFocus={() => setEmailActive(true)}
                  onBlur={() => { 
                    setEmailActive(!!formData.email); 
                    handleBlur('email');
                  }}
                  placeholder="admin@resumate.com"
                  aria-describedby="emailHelp"
                  disabled={loading}
                  onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                />
                {errors.email && touched.email && (
                  <div className="invalid-feedback d-block">{errors.email}</div>
                )}
              </div>

              <div className={`form-group position-relative ${passwordActive ? "active" : ""}`}>
                <label htmlFor="password">Password</label>
                <input
                  type={showPassword ? "text" : "password"}
                  className={`form-control ${errors.password && touched.password ? "is-invalid" : ""}`}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handlePasswordChange}
                  onFocus={() => setPasswordActive(true)}
                  onBlur={() => { 
                    setPasswordActive(!!formData.password); 
                    handleBlur('password');
                  }}
                  disabled={loading}
                  onKeyDown={(e) => { if (e.key === " ") e.preventDefault(); }}
                />
                <span 
                  className="pass-show" 
                  onClick={togglePasswordVisibility} 
                  style={{ cursor: 'pointer' }}
                >
                  {showPassword ? (
                    <img src="/images/view.svg" alt="Hide password" />
                  ) : (
                    <img src="/images/show-pass.svg" alt="Show password" />
                  )}
                </span>
                {errors.password && touched.password && (
                  <div className="invalid-feedback d-block">{errors.password}</div>
                )}
              </div>

              <div className="form-group form-check">
                <Link to="/admin/forgotPassword">
                  <a className="forgot">Forgot Password?</a>
                </Link>
              </div>

              <button 
                type="submit" 
                className="btn" 
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Logging in...
                  </>
                ) : (
                  "Login"
                )}
              </button>
            </form>
          </div>

          <h4>
            Don't have access? <a href="#">Contact Super Admin.</a>
          </h4>
        </div>
      </div>

      <div className="login-left right">
        <img src="/images/login-bg.png" className="img-fluid" alt="Login Background" />
      </div>
    </main>
    </>
  );
};

export default Login;
